import SafeLocalStorage from 'safe-localstorage';
import { defineStore } from 'pinia';
import { api } from 'services/api';
import { useAccountStore } from '#src/store/account';
import { useDayjs } from 'composables/dayjs';
import { friendlyDuration } from 'composables/format';
import Trip from '@models/Trip';

type TripViewMode = 'simple' | 'compact' | 'full';

export const useTripStore = defineStore({
  id: 'trip',
  state: () => {
    const tripViewMode = <TripViewMode>SafeLocalStorage.get('trip/tripViewMode') || 'simple';
    return {
      cursor: undefined as string | undefined,
      hasLoadedTrips: false,
      isLoading: false,
      selectedTripIndex: 0,
      trips: [] as Trip[],
      tripViewMode: tripViewMode,
      vehicleKey: '',
    };
  },
  getters: {
    formattedTrips(state) {
      return state.trips.map((trip) => {
        const { durationSeconds, idleSeconds, idleStopSeconds } = trip;

        const totalIdleSeconds = (idleSeconds || 0) + (idleStopSeconds || 0);
        const [idleTime, idleUm] = friendlyDuration(totalIdleSeconds);

        const [durationTime, durationUm] = friendlyDuration(<number>durationSeconds);

        return {
          ...trip,
          durationTime,
          durationUm,
          idleTime,
          idleUm,
          totalIdleSeconds,
        };
      });
    },
    hasMore(state) {
      return Boolean(state.cursor);
    },
    placeholderTrips() {
      return [...(<Trip[]>new Array(10))].map((_, index) => ({
        gpsDistance: 0,
        distanceUm: '',
        durationTime: 0,
        durationUm: '',
        endPoint: null,
        hardBrakeCount: 0,
        idleTime: 0,
        idleUm: '',
        key: index,
        speedUm: '',
        startPoint: null,
        topSpeed: 0,
      }));
    },
    selectedTrip(state) {
      return state.trips[state.selectedTripIndex];
    },
  },
  actions: {
    clearTrips() {
      this.cursor = undefined;
      this.hasLoadedTrips = false;
      this.trips = [];
      this.vehicleKey = '';
    },
    async getTrips(vehicleKey: string, loadNext?: boolean) {
      const { currentAccount } = useAccountStore();

      this.vehicleKey = vehicleKey;

      const cursor = loadNext && this.cursor ? `&cursor=${this.cursor}` : '';

      this.isLoading = true;

      try {
        const { data } = await api.get<{ cursor?: string; trips: ApiTrip[] }>(
          `/vehicle/${vehicleKey}/trips?accountKey=${<string>currentAccount}${cursor}`
        );
        const { trips } = data;
        this.cursor = data.cursor;
        this.insertTrips(trips);
      } catch (error) {
        console.error(error);
      }

      this.hasLoadedTrips = true;

      this.isLoading = false;
    },
    insertTrips(tripsData: ApiTrip[]) {
      const { dayjs } = useDayjs();

      const trips = tripsData.map((trip) => {
        const { timestamp: startTimestamp, timestamp_utc: startTimestampUtc } = trip.start_point || {};
        const { timestamp: endTimestamp, timestamp_utc: endTimestampUtc } = trip.end_point || {};

        let endPoint = null;
        if (trip.end_point) {
          endPoint = {
            fuelLevel: trip.end_point?.fuel_level,
            latitude: trip.end_point?.latitude,
            longitude: trip.end_point?.longitude,
            timestamp: endTimestamp ? dayjs(endTimestamp) : null,
            timestampTz: trip.end_point?.timestamp_tz,
            timestampUtc: endTimestampUtc ? dayjs(endTimestampUtc) : null,
          };
        }

        let startPoint = null;
        if (trip.start_point) {
          startPoint = {
            fuelLevel: trip.start_point?.fuel_level,
            latitude: trip.start_point?.latitude,
            longitude: trip.start_point?.longitude,
            timestamp: startTimestamp ? dayjs(startTimestamp) : null,
            timestampTz: trip.start_point?.timestamp_tz,
            timestampUtc: startTimestampUtc ? dayjs(startTimestampUtc) : null,
          };
        }

        return {
          distanceUm: trip.distance_um,
          durationSeconds: trip.duration_seconds,
          encodedPolyline: trip.encoded_polyline,
          endPoint,
          gpsDistance: trip.gps_distance,
          gpsMiles: trip.gps_miles,
          hardBrakeCount: trip.hard_brake_count,
          idleSeconds: trip.idle_seconds,
          idleStopSeconds: trip.idle_stop_seconds,
          key: trip.key,
          speedUm: trip.speed_um,
          speedingCityMajorCount: trip.speeding_city_major_count,
          speedingCityMinorCount: trip.speeding_city_minor_count,
          speedingHwyMajorCount: trip.speeding_hwy_major_count,
          speedingHwyMinorCount: trip.speeding_hwy_minor_count,
          startPoint,
          topSpeed: trip.top_speed,
          topSpeedMph: trip.top_speed_mph,
          user: trip.user,
          vehicle: trip.vehicle,
        };
      });

      this.trips = [...this.trips, ...trips];
    },
    loadMoreTrips() {
      return this.getTrips(this.vehicleKey, true);
    },
    selectTripIndex(index: number) {
      this.selectedTripIndex = index;
    },
  },
});
